import {
  Badge, Button, Container, Group, Pagination, Stack,
  Table, Text, Title, Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconX, IconBan, IconFilterOff } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useChangeRequestsList, useApproveChangeRequest, useRejectChangeRequest, useCancelChangeRequest } from '../hooks/useChangeRequests';
import { CR_ENTITY_TYPE_OPTIONS, CR_KIND_OPTIONS } from '../api/changeRequests';
import {
  ColumnDateRangeFilter, ColumnMultiSelectFilter, hasActiveFilters,
} from '../components/ColumnFilters';
import { SortableTh, nextSort, sortParam, type SortState } from '../components/SortableHeader';
import { StatusExplained } from '../components/StatusExplained';
import { BackToCompliance } from '../components/BackToCompliance';
import { useStaffOptions } from '../hooks/useAgreementInteractions';
import { useCurrentUser } from '../hooks/useAuth';
import type { ChangeRequestStatus, UserRole } from '../api/types';
import { RequireAccess } from '../components/RequireAccess';
import { CHANGE_REQUEST_KIND_LABELS, CHANGE_REQUEST_STATUS_LABELS } from '../api/types';

const PAGE_SIZE = 50;
type CrSortKey = 'kind' | 'entity' | 'status' | 'requested' | 'requester';

// #49 (2026-06-13, §3j): map the raw entity_type to its friendly label
// from the same closed option set the header filter uses; fall back to the
// raw string when it isn't in the list (e.g. a legacy/unknown type).
const ENTITY_TYPE_LABEL = new Map(
  CR_ENTITY_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);
function entityTypeLabel(entityType: string): string {
  return ENTITY_TYPE_LABEL.get(entityType) ?? entityType;
}

/** C-356 / C-358 (Pedro 2026-10-11): the review queue is Compliance area — Admin and
 *  Compliance only. The server already shows other roles only their own changes, which
 *  they see on My Change Requests; gating the page keeps the areas consistent. */
export const CHANGE_REQUESTS_PAGE_ROLES: UserRole[] = ['admin', 'compliance'];

export function ChangeRequestsListPage() {
  return (
    <RequireAccess roles={CHANGE_REQUESTS_PAGE_ROLES}
      message="This area is available to Admin and Compliance only. Your own filed changes are under My Change Requests.">
      <ChangeRequestsListBody />
    </RequireAccess>
  );
}

function ChangeRequestsListBody() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const isAdmin = user?.role === 'admin';
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ChangeRequestStatus | undefined>('PENDING');

  // ── #47 (2026-06-12): per-column predicate filters (C-132). The status
  // buttons above the table stay as the POPULATION selector; the Status
  // column gets NO header filter (one axis, one control).
  const [kindF, setKindF] = useState<string[]>([]);
  const [entityF, setEntityF] = useState<string[]>([]);
  const [reqFrom, setReqFrom] = useState('');
  const [reqTo, setReqTo] = useState('');
  // C-132 Amendment 3 (2026-06-12, Pedro): people-pickers are multi too.
  const [requesterF, setRequesterF] = useState<string[]>([]);
  // P41 phase 2 (2026-10-15): header sort, server-side across pages; null = newest first.
  const [sort, setSortState] = useState<SortState<CrSortKey> | null>(null);
  function onSort(k: CrSortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }

  const filtersActive = hasActiveFilters({ kindF, entityF, reqFrom, reqTo, requesterF });
  function clearFilters() {
    setKindF([]); setEntityF([]); setReqFrom(''); setReqTo(''); setRequesterF([]);
    setPage(1);
  }
  function paged<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  const staffQ = useStaffOptions();
  const requesterOptions = (Array.isArray(staffQ.data) ? staffQ.data : [])
    .map((u) => ({ value: u.id, label: u.display_name }));

  const { data, isLoading, error } = useChangeRequestsList(statusFilter, undefined, {
    kinds: kindF.length > 0 ? kindF : undefined,
    entity_types: entityF.length > 0 ? entityF : undefined,
    requested_from: reqFrom || undefined,
    requested_to: reqTo || undefined,
    requested_by_user_id: requesterF.length > 0 ? requesterF : undefined,
    sort: sortParam(sort),
    offset: (page - 1) * PAGE_SIZE,
  });
  const approve = useApproveChangeRequest();
  const reject = useRejectChangeRequest();
  const cancel = useCancelChangeRequest();
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const statusColor = (s: ChangeRequestStatus) => {
    switch (s) {
      case 'PENDING': return 'yellow';
      case 'APPLIED': return 'green';
      case 'REJECTED': return 'red';
      case 'CANCELLED': return 'gray';
    }
  };

  return (
    <Container size="xl">
      <Stack>
        <BackToCompliance />
        <Title order={2}>Change requests (Maker-checker queue)</Title>

        <Group>
          <Text>Filter by status:</Text>
          {/* P47: friendly labels (was the raw PENDING / APPLIED…) and a hover meaning. */}
          {(['PENDING', 'APPLIED', 'REJECTED', 'CANCELLED'] as ChangeRequestStatus[]).map((s) => (
            <StatusExplained key={s} entity="change_request" status={s}>
              <Button
                variant={statusFilter === s ? 'filled' : 'outline'}
                size="xs"
                onClick={() => { setStatusFilter(s); setPage(1); }}
              >
                {CHANGE_REQUEST_STATUS_LABELS[s] ?? s}
              </Button>
            </StatusExplained>
          ))}
          <StatusExplained entity="change_request" status="ALL">
            <Button
              variant={statusFilter === undefined ? 'filled' : 'outline'}
              size="xs"
              onClick={() => { setStatusFilter(undefined); setPage(1); }}
            >
              All
            </Button>
          </StatusExplained>
        </Group>

        {error && <Text c="red">{error.detail}</Text>}

        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              {/* P41 phase 2 (2026-10-15): every header sorts (server-side). */}
              <SortableTh label="Kind" col="kind" sort={sort} onSort={onSort} w={110}
                filtered={kindF.length > 0} />
              <SortableTh label="Entity type" col="entity" sort={sort} onSort={onSort} w={170}
                filtered={entityF.length > 0} />
              <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w={120} />
              <SortableTh label="Requested at" col="requested" sort={sort} onSort={onSort} w={150}
                filtered={reqFrom !== '' || reqTo !== ''} />
              <SortableTh label="Requested by" col="requester" sort={sort} onSort={onSort} w={150}
                filtered={requesterF.length > 0} />
              <Table.Th w={320}>Actions</Table.Th>
            </Table.Tr>
            {/* #47: per-column filter row (C-132). Status is owned by the
                population buttons above — no header filter. */}
            <Table.Tr>
              <Table.Th>
                <ColumnMultiSelectFilter values={kindF} onChange={paged(setKindF)}
                  options={CR_KIND_OPTIONS} allLabel="All" ariaLabel="Filter by kind" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={entityF} onChange={paged(setEntityF)}
                  options={CR_ENTITY_TYPE_OPTIONS} allLabel="All" ariaLabel="Filter by entity type" />
              </Table.Th>
              <Table.Th />
              <Table.Th>
                <ColumnDateRangeFilter from={reqFrom} to={reqTo}
                  onChange={(f, t) => { setReqFrom(f); setReqTo(t); setPage(1); }}
                  ariaLabelPrefix="Requested" />
              </Table.Th>
              <Table.Th>
                {/* #4 (§3j): the requester filter's options come from an
                    admin-only source, so it's a dead control for non-admins. */}
                {isAdmin && (
                  <ColumnMultiSelectFilter values={requesterF} onChange={paged(setRequesterF)}
                    options={requesterOptions} allLabel="Anyone" ariaLabel="Filter by requester" />
                )}
              </Table.Th>
              <Table.Th>
                {filtersActive && (
                  <Tooltip label="Clear all column filters" withArrow>
                    <Button size="compact-xs" variant="subtle" color="gray"
                      leftSection={<IconFilterOff size={14} />}
                      onClick={clearFilters}>
                      Clear
                    </Button>
                  </Tooltip>
                )}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && <Table.Tr><Table.Td colSpan={6}><Text c="dimmed">Loading…</Text></Table.Td></Table.Tr>}
            {/* #47 / C-132: two-tier empty state. */}
            {!isLoading && data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  {filtersActive ? (
                    <Group gap="xs" justify="center" py="sm">
                      <Text c="dimmed" size="sm">No change requests match the current filters.</Text>
                      <Button size="compact-xs" variant="subtle"
                        leftSection={<IconFilterOff size={14} />}
                        onClick={clearFilters}>
                        Clear filters
                      </Button>
                    </Group>
                  ) : (
                    <Text c="dimmed">No change requests.</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {data?.items.map((cr) => (
              <Table.Tr key={cr.id} style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/change-requests/${cr.id}`)}>
                <Table.Td><Badge variant="light">{CHANGE_REQUEST_KIND_LABELS[cr.kind] ?? cr.kind}</Badge></Table.Td>
                {/* #49 (2026-06-13, C-134, §3j): humans read names, not table
                    names. When the server resolved a friendly label for the
                    TARGET record (works even for soft-deleted DELETE targets),
                    show it as the primary line with the entity-type as a small
                    dimmed subtitle. Otherwise fall back to the friendly
                    entity-type label (then the raw string). */}
                <Table.Td>
                  {cr.entity_friendly_label ? (
                    <>
                      <Text size="sm">{cr.entity_friendly_label}</Text>
                      <Text size="xs" c="dimmed">{entityTypeLabel(cr.entity_type)}</Text>
                    </>
                  ) : (
                    <Text size="sm">{entityTypeLabel(cr.entity_type)}</Text>
                  )}
                  {/* Phase 5 (C-160): high-impact flag so the checker sees the
                      blast radius before opening the CR. */}
                  {cr.impact?.is_high_impact && (
                    <Badge size="xs" color="orange" variant="light" mt={4}
                      leftSection={<IconAlertTriangle size={11} />}>
                      High impact
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <StatusExplained entity="change_request" status={cr.status}>
                    <Badge color={statusColor(cr.status)}>{CHANGE_REQUEST_STATUS_LABELS[cr.status] ?? cr.status}</Badge>
                  </StatusExplained>
                </Table.Td>
                {/* Pedro (2026-06-10): queue rows need date AND time —
                    several CRs can land the same day; ordering must be
                    visible. Same format as MyChangeRequestsPage. */}
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {cr.requested_at
                      ? cr.requested_at.substring(0, 10) + ' ' + cr.requested_at.substring(11, 16)
                      : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>{cr.requested_by_name ?? cr.requested_by?.substring(0, 8)}</Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  {/* C-117 (2026-06-10): these commit IMMEDIATELY (no
                      staging net), so per the convention they are labeled
                      buttons, never bare icons. */}
                  {cr.status === 'PENDING' && (
                    <Group gap="xs" wrap="nowrap">
                      {/* Phase 5 (C-160): a high-impact CR cannot be approved
                          one-click from the queue — it routes to the detail
                          page where the approve-confirm gate lives. Reject +
                          Cancel stay one-click (Pedro: one-click reject). */}
                      {cr.impact?.is_high_impact ? (
                        <Button size="xs" variant="light" color="orange"
                          leftSection={<IconAlertTriangle size={14} />}
                          onClick={() => navigate(`/change-requests/${cr.id}`)}>
                          Review
                        </Button>
                      ) : (
                        <Button size="xs" variant="light" color="green"
                          leftSection={<IconCheck size={14} />}
                          onClick={() => approve.mutate({ id: cr.id })}>
                          Approve
                        </Button>
                      )}
                      <Button size="xs" variant="light" color="red"
                        leftSection={<IconX size={14} />}
                        onClick={() => reject.mutate({ id: cr.id })}>
                        Reject
                      </Button>
                      <Button size="xs" variant="light" color="gray"
                        leftSection={<IconBan size={14} />}
                        onClick={() => cancel.mutate({ id: cr.id })}>
                        Cancel
                      </Button>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {totalPages > 1 && <Pagination value={page} onChange={setPage} total={totalPages} />}
      </Stack>
    </Container>
  );
}
